/**
 * HUD anchor + offset system (T-4.3).
 *
 * Pure functions for computing pixel positions of HUD elements relative to the
 * canvas / playfield bounds. Exists so DOM HUD overlays (`HUD.tsx`,
 * `DailyShotCounter.tsx`, `BombIndicator.tsx`, ...) and future Pixi-side HUD
 * sprites share one source of truth for "score: top-left + 12px", "pause:
 * top-right - 12px", etc.
 *
 * Why a module instead of inline `top: 0; right: 0`:
 *   - The Steam port (T-4.3) introduces dynamic canvas sizing via Pixi
 *     `resizeTo` + ResizeObserver. The DOM-only `top: 0` works on the GameScreen
 *     container because the container fills the viewport, but anchor math here
 *     is keyed off the *playfield* rect (canvas minus letterbox bars), which
 *     can be inset within the container on ultrawide displays.
 *   - Tests want pixel-precise assertions (e.g. "score lands at (12, 12) when
 *     canvas is 1920×1080 with anchor 'top-left'"). Inline CSS is hard to
 *     unit-test; a pure helper is trivial.
 *
 * Engine-purity: this module lives under `src/renderer/`, not `src/game/`. It
 * uses no `Math.random`, `Date.now`, or `performance.now` — all inputs are
 * deterministic. Replay paths never invoke this code.
 *
 * Interaction with T-4.4 letterbox:
 *   `computePlayfieldRect` derives the inset rect using the same playfield
 *   aspect as `detectLetterboxBars`. Two callers (this helper + the React
 *   `LetterboxFrame`) reach the same letterbox-bar widths to within rounding,
 *   so HUD elements at corner anchors land flush with the brass frame edge
 *   and never inside a letterbox bar.
 */

/**
 * Native logical playfield dimensions. The engine renders at 375×667 and
 * `src/renderer/setup.ts` scales the Pixi stage uniformly; HUD anchor math
 * uses the *displayed* (post-scale) playfield rect, not the logical one.
 *
 * Source: `src/game/config.ts` BOARD_WIDTH_PX × BOARD_HEIGHT_PX. We hard-pin
 * these constants here because importing `@/game/config.js` from a
 * renderer-layer helper would re-establish the engine→renderer dependency
 * direction we deliberately avoid (renderer depends on engine, not vice
 * versa, but pure-helper modules under `src/renderer/` should be free of
 * engine imports so they test in isolation).
 */
export const PLAYFIELD_NATIVE_WIDTH = 375;
export const PLAYFIELD_NATIVE_HEIGHT = 667;

/**
 * Native playfield aspect ratio (width / height) — 9:16 portrait.
 *
 * The T-4.4 spec discrepancy (`steam-port/docs/T-4.4.md` "Spec discrepancy")
 * is resolved here: the playfield is 9:16, period. `LetterboxFrame.tsx`
 * defaults to 16:9 for backwards compat with its existing test matrix; T-4.3
 * uses 9:16 for the actual playfield-rect math.
 */
export const PLAYFIELD_ASPECT = PLAYFIELD_NATIVE_WIDTH / PLAYFIELD_NATIVE_HEIGHT;

export type HudAnchor =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'center';

export interface PlayfieldRect {
  /** Pixel x-coordinate of the playfield's left edge within the canvas. */
  readonly x: number;
  /** Pixel y-coordinate of the playfield's top edge within the canvas. */
  readonly y: number;
  /** Pixel width of the displayed playfield (uniformly scaled). */
  readonly w: number;
  /** Pixel height of the displayed playfield (uniformly scaled). */
  readonly h: number;
  /**
   * Uniform scale factor applied to the logical 375×667 playfield to produce
   * the displayed `w` / `h`. Pixi's stage.scale is set to this value; Pixi
   * stage.position is set to `(x, y)`.
   */
  readonly scale: number;
  /** Letterbox bar width on each side, in pixels. 0 unless canvas is wider than playfield. */
  readonly letterboxX: number;
  /** Letterbox bar height on each side, in pixels. 0 unless canvas is taller than playfield. */
  readonly letterboxY: number;
}

/**
 * Compute the pixel rect of the playfield within a given canvas size.
 *
 * Uniform-scale fit: `scale = min(canvasW / 375, canvasH / 667)`. The
 * playfield is centered in whichever axis has slack; the slack pixels become
 * letterbox bars (which T-4.4 paints decoratively).
 *
 * Defensive guards:
 *   - canvasW / canvasH ≤ 0 or non-finite → returns degenerate `{x:0,y:0,w:0,h:0,scale:0,...}`.
 *     Browsers fire `resize` with 0×0 during minimize transitions on some
 *     platforms; the renderer must not divide by zero.
 */
export function computePlayfieldRect(
  canvasW: number,
  canvasH: number,
  nativeW: number = PLAYFIELD_NATIVE_WIDTH,
  nativeH: number = PLAYFIELD_NATIVE_HEIGHT,
): PlayfieldRect {
  if (
    !Number.isFinite(canvasW) ||
    !Number.isFinite(canvasH) ||
    !Number.isFinite(nativeW) ||
    !Number.isFinite(nativeH) ||
    canvasW <= 0 ||
    canvasH <= 0 ||
    nativeW <= 0 ||
    nativeH <= 0
  ) {
    return { x: 0, y: 0, w: 0, h: 0, scale: 0, letterboxX: 0, letterboxY: 0 };
  }

  const scale = Math.min(canvasW / nativeW, canvasH / nativeH);
  const w = nativeW * scale;
  const h = nativeH * scale;
  const letterboxX = Math.max(0, (canvasW - w) / 2);
  const letterboxY = Math.max(0, (canvasH - h) / 2);

  return {
    x: letterboxX,
    y: letterboxY,
    w,
    h,
    scale,
    letterboxX,
    letterboxY,
  };
}

// ---------------------------------------------------------------------------
// Game-screen layout: playfield + HUD band + frame bars.
// ---------------------------------------------------------------------------

/** HUD text scale: tracks the board's scale, clamped so the readouts never
 * shrink below the phone baseline or balloon on 4K. */
export const HUD_SCALE_MIN = 1;
export const HUD_SCALE_MAX = 1.8;

/**
 * Minimum letterbox column width (in unscaled px, multiplied by the HUD
 * scale) before the score plate hangs outside the board in the left margin
 * and the pause button in the right one (desktop / Steam).
 */
export const PLATE_OUTSIDE_MIN_ROOM = 170;
export const PLATE_GAP = 12;

/**
 * Height of the HUD band (in unscaled px, multiplied by the HUD scale) that
 * is reserved above the board when the plate can't hang in a side column.
 * The top rows of gears start at the board's very top edge, so a plate laid
 * over the board hides the gears the player most needs to read. The band
 * is carved out of the vertical slack first; the board only shrinks on a
 * viewport with no slack to give (a 375×667 phone loses ~7% of its scale).
 */
export const HUD_BAND_NATIVE_PX = 48;

export interface GameLayout {
  /** Displayed board rect, in px from the container's top-left. */
  readonly playfield: PlayfieldRect;
  /** HUD text scale (`HUD_SCALE_MIN`..`HUD_SCALE_MAX`). */
  readonly hudScale: number;
  /** True when the plate and pause button hang in the side columns. */
  readonly plateOutside: boolean;
  /** Height of the HUD band directly above the board; 0 when `plateOutside`. */
  readonly hudBandPx: number;
  /** Frame bar thicknesses. Top includes the HUD band. */
  readonly topBarPx: number;
  readonly bottomBarPx: number;
  readonly sideBarPx: number;
}

export function clampHudScale(scale: number): number {
  return Math.min(HUD_SCALE_MAX, Math.max(HUD_SCALE_MIN, scale));
}

/**
 * Lay the game screen out in a container of `w`×`h` px (the safe area).
 * `computePlayfieldRect` centres the board in the box; this adds the HUD's
 * placement so the board, the HUD and the decorative frame all agree on
 * where the edges are.
 */
export function computeGameLayout(w: number, h: number): GameLayout {
  const base = computePlayfieldRect(w, h);
  const hudScale = clampHudScale(base.scale);
  if (base.letterboxX >= PLATE_OUTSIDE_MIN_ROOM * hudScale + PLATE_GAP) {
    return {
      playfield: base,
      hudScale,
      plateOutside: true,
      hudBandPx: 0,
      topBarPx: Math.floor(base.letterboxY),
      bottomBarPx: Math.floor(base.letterboxY),
      sideBarPx: Math.floor(base.letterboxX),
    };
  }
  const hudBandPx = Math.round(HUD_BAND_NATIVE_PX * hudScale);
  const inner = computePlayfieldRect(w, Math.max(0, h - hudBandPx));
  const playfield: PlayfieldRect = { ...inner, y: inner.y + hudBandPx };
  return {
    playfield,
    hudScale,
    plateOutside: false,
    hudBandPx,
    topBarPx: Math.floor(playfield.y),
    bottomBarPx: Math.max(0, Math.floor(h - playfield.y - playfield.h)),
    sideBarPx: Math.floor(inner.letterboxX),
  };
}

/**
 * Compute the pixel position of a HUD element given its anchor + offset.
 *
 * `offsetX` / `offsetY` are interpreted as "inset from the anchor edge" — for
 * top/left anchors they shift right/down (positive values), for bottom/right
 * anchors they shift left/up (positive values still mean "further from the
 * edge"). This mirrors how CSS authors think about `top: Npx; right: Npx` —
 * the user types `12` and gets a 12px margin from the corresponding edge no
 * matter which corner.
 *
 * `bounds` is typically the canvas size for canvas-anchored HUD (pause
 * button), or a `PlayfieldRect` for playfield-anchored HUD (score readout).
 * The caller decides which is appropriate per element.
 */
export interface AnchorBounds {
  /** Top-left x-coordinate of the anchoring rect (typically 0 for canvas). */
  readonly x: number;
  /** Top-left y-coordinate of the anchoring rect (typically 0 for canvas). */
  readonly y: number;
  /** Width of the anchoring rect. */
  readonly w: number;
  /** Height of the anchoring rect. */
  readonly h: number;
}

export function computeAnchorPosition(
  bounds: AnchorBounds,
  anchor: HudAnchor,
  offsetX: number = 0,
  offsetY: number = 0,
): { x: number; y: number } {
  const { x, y, w, h } = bounds;
  switch (anchor) {
    case 'top-left':
      return { x: x + offsetX, y: y + offsetY };
    case 'top-right':
      return { x: x + w - offsetX, y: y + offsetY };
    case 'bottom-left':
      return { x: x + offsetX, y: y + h - offsetY };
    case 'bottom-right':
      return { x: x + w - offsetX, y: y + h - offsetY };
    case 'center':
      return { x: x + w / 2 + offsetX, y: y + h / 2 + offsetY };
  }
}

/**
 * HUD element registry: declarative table of every HUD overlay's anchor +
 * offset, used by tests + future Pixi-sprite HUD. DOM HUD elements (HUD.tsx,
 * DailyShotCounter.tsx) currently use CSS `position: absolute` with the same
 * anchor semantics — this table is the canonical source so future
 * Pixi-side HUD work doesn't reinvent the offsets.
 *
 * Padding values match the existing CSS `padding: 8px 12px` on `<HUD>` and
 * `padding: 6px 12px` on the pause button — keeping pixel parity with the
 * shipped HUD so users see no visual change from this refactor.
 */
export interface HudElementSpec {
  readonly id: string;
  readonly anchor: HudAnchor;
  readonly offsetX: number;
  readonly offsetY: number;
  /**
   * Whether this element anchors to the canvas as a whole or to the
   * playfield rect (canvas minus letterbox bars). Most HUD elements anchor
   * to the playfield so they don't float into the letterbox columns on
   * ultrawide displays.
   */
  readonly relativeTo: 'canvas' | 'playfield';
}

export const HUD_ELEMENTS: readonly HudElementSpec[] = [
  // Score + multiplier plate (HUD.tsx). Inside the playfield's top-left
  // corner on narrow viewports; when the letterbox column is wide enough
  // (desktop / Steam) HUD.tsx hangs the plate just outside the playfield's
  // left edge instead, and the pause button outside its right edge.
  // Offsets below are the inside-the-board placement at 1x HUD scale.
  { id: 'score', anchor: 'top-left', offsetX: 12, offsetY: 8, relativeTo: 'playfield' },
  { id: 'multiplier', anchor: 'top-left', offsetX: 12, offsetY: 56, relativeTo: 'playfield' },
  // Shot clock (HUD.tsx top center).
  { id: 'shot-clock', anchor: 'top-left', offsetX: 0, offsetY: 8, relativeTo: 'playfield' },
  // Pause button (HUD.tsx top row, right side).
  { id: 'pause', anchor: 'top-right', offsetX: 12, offsetY: 8, relativeTo: 'playfield' },
  // Daily shot counter (DailyShotCounter.tsx).
  { id: 'daily-shot-counter', anchor: 'bottom-left', offsetX: 12, offsetY: 12, relativeTo: 'playfield' },
  // Bomb indicator (BombIndicator.tsx, queue display).
  { id: 'bomb-indicator', anchor: 'bottom-right', offsetX: 12, offsetY: 12, relativeTo: 'playfield' },
  // Score-frenzy indicator (ScoreFrenzyIndicator.tsx, center burst).
  { id: 'score-frenzy', anchor: 'center', offsetX: 0, offsetY: 0, relativeTo: 'playfield' },
] as const;
